import { useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import InputField from "../../../../Components/InputField";
import SingleSelectDropdown from "../../../../Components/CustomDropdowns/SingleSelectDropdown";
import projectSchema from "./projectSchema";
import { PROJECT_TYPES } from "../../../../mocks/communityProjects";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/webp",
];

const labelStyles = {
  fontSize: "15px",
  fontWeight: 500,
  color: "rgba(47, 43, 61, 0.9)",
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const helpIconStyles = {
  width: "16px",
  height: "16px",
  cursor: "pointer",
  verticalAlign: "middle",
};

// Small dark corner button to remove an image (matches the Figma).
const cornerRemoveSx = {
  position: "absolute",
  top: "6px",
  insetInlineEnd: "6px",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "#fff",
  padding: "2px",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
};

// "Add image" tile — mirrors the existing product modal's dashed upload tile
// (ManagePricelists/Content/ProductsContent) for cross-app consistency.
const uploadTileSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  cursor: "pointer",
  textTransform: "none",
  borderRadius: "8px",
  border: "1px dashed #BDBDBD",
  backgroundColor: "transparent",
  "&:hover": {
    borderColor: "#248B70",
    backgroundColor: "rgba(36, 139, 112, 0.04)",
  },
};

const ProjectModal = (props) => {
  const { open, mode, project, onClose, onSubmit } = props;
  const { t } = useTranslation("page-community-settings");

  const [submitting, setSubmitting] = useState(false);

  const isEdit = mode === "edit";

  const initialValues = {
    name: project?.name || "",
    type: project?.type || "",
    description: project?.description || "",
    terms: project?.terms || "",
    fundingGoal: project?.fundingGoal ?? "",
    mainImage: project?.mainImage || "",
    extraImages: project?.extraImages || [],
  };

  const typeOptions = [
    {
      value: PROJECT_TYPES.IN_SYSTEM,
      label: t("manage_community_projects.modal.type_in_system"),
    },
    {
      value: PROJECT_TYPES.MANAGER_DONATION,
      label: t("manage_community_projects.modal.type_manager_donation"),
    },
  ];

  const typeTooltipContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
          {t("manage_community_projects.modal.type_tooltip_in_system_title")}
        </Typography>
        <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "#fff" }}>
          {t("manage_community_projects.modal.type_tooltip_in_system_body")}
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
          {t(
            "manage_community_projects.modal.type_tooltip_manager_donation_title",
          )}
        </Typography>
        <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "#fff" }}>
          {t(
            "manage_community_projects.modal.type_tooltip_manager_donation_body",
          )}
        </Typography>
      </Box>
    </Box>
  );

  const handleSubmit = async (values) => {
    setSubmitting(true);
    // Brief simulated loading state (mock — no real persistence).
    await new Promise((resolve) => setTimeout(resolve, 300));
    onSubmit({
      ...values,
      fundingGoal: Number(values.fundingGoal),
    });
    setSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxWidth: "960px",
          width: "100%",
        },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={projectSchema(t)}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange
        enableReinitialize
      >
        {({
          values,
          errors,
          submitCount,
          handleChange,
          handleBlur,
          handleSubmit: formikSubmit,
          setFieldValue,
          setFieldTouched,
        }) => {
          // Show validation errors only after a save attempt; they then clear
          // live as the user fixes each field (validateOnChange is on).
          const showError = (field) => Boolean(submitCount > 0 && errors[field]);
          const errorText = (field) =>
            submitCount > 0 && errors[field] ? errors[field] : "";

          const handleMainImagePick = (file) => {
            if (!file) return;
            // Set the value AND re-validate immediately so the "required" error
            // clears — validateOnChange is off and an image picker has no blur.
            setFieldValue("mainImage", URL.createObjectURL(file), true);
            setFieldTouched("mainImage", true, false);
          };

          const handleAddExtraImage = (file) => {
            if (!file) return;
            const url = URL.createObjectURL(file);
            setFieldValue("extraImages", [...(values.extraImages || []), url]);
          };

          const handleRemoveExtraImage = (idx) => {
            const next = [...(values.extraImages || [])];
            next.splice(idx, 1);
            setFieldValue("extraImages", next);
          };

          return (
            <form onSubmit={formikSubmit}>
              {/* Header */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  px: "24px",
                  py: "18px",
                  borderBottom: "1px solid #EAEAEA",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    color: "rgba(47, 43, 61, 0.9)",
                  }}
                >
                  {isEdit
                    ? t("manage_community_projects.modal.edit_title")
                    : t("manage_community_projects.modal.add_title")}
                </Typography>

                <IconButton onClick={onClose} size="small">
                  <CloseIcon
                    fontSize="small"
                    sx={{ color: "rgba(62, 63, 76, 1)" }}
                  />
                </IconButton>
              </Box>

              {/* Content */}
              <Box
                sx={{
                  px: "24px",
                  py: "24px",
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: "24px",
                  }}
                >
                  {/* LEFT column */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    <InputField
                      label={`${t("manage_community_projects.modal.name_label")} *`}
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={t(
                        "manage_community_projects.modal.name_placeholder",
                      )}
                      error={showError("name")}
                      helperText={errorText("name")}
                    />

                    {/* Project Type */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <Typography sx={labelStyles}>
                        {`${t("manage_community_projects.modal.type_label")} *`}
                        <Tooltip
                          arrow
                          placement="top"
                          title={typeTooltipContent}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: "#2F2B3D",
                                padding: "12px",
                                borderRadius: "8px",
                                maxWidth: 320,
                              },
                            },
                            arrow: { sx: { color: "#2F2B3D" } },
                          }}
                        >
                          <img
                            src="/icons/help-filled-icon.svg"
                            alt="help"
                            style={helpIconStyles}
                          />
                        </Tooltip>
                      </Typography>
                      <SingleSelectDropdown
                        value={values.type}
                        onChange={(val) => setFieldValue("type", val)}
                        onBlur={() => setFieldTouched("type", true)}
                        options={typeOptions}
                        placeholder={t(
                          "manage_community_projects.modal.type_label",
                        )}
                        error={showError("type")}
                        helperText={errorText("type")}
                      />
                    </Box>

                    <InputField
                      label={t(
                        "manage_community_projects.modal.description_label",
                      )}
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={t(
                        "manage_community_projects.modal.description_placeholder",
                      )}
                      multiline
                      rows={4}
                    />

                    <InputField
                      label={t("manage_community_projects.modal.terms_label")}
                      name="terms"
                      value={values.terms}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={t(
                        "manage_community_projects.modal.terms_placeholder",
                      )}
                      multiline
                      rows={4}
                    />

                    <InputField
                      label={`${t("manage_community_projects.modal.funding_goal_label")} *`}
                      name="fundingGoal"
                      value={values.fundingGoal}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      type="number"
                      error={showError("fundingGoal")}
                      helperText={errorText("fundingGoal")}
                      InputProps={{
                        startAdornment: (
                          <Box
                            component="span"
                            sx={{
                              pl: "12px",
                              color: "rgba(47, 43, 61, 0.7)",
                              fontSize: "15px",
                            }}
                          >
                            ₪
                          </Box>
                        ),
                      }}
                    />
                  </Box>

                  {/* RIGHT column */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "24px",
                    }}
                  >
                    {/* Main image */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <Typography sx={labelStyles}>
                        {`${t("manage_community_projects.modal.main_image_label")} *`}
                      </Typography>
                      {values.mainImage ? (
                        <Box
                          sx={{
                            position: "relative",
                            width: 200,
                            height: 120,
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "1px solid #EAEAEA",
                          }}
                        >
                          <img
                            src={values.mainImage}
                            alt="main"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <IconButton
                            onClick={() => setFieldValue("mainImage", "", true)}
                            size="small"
                            sx={cornerRemoveSx}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          component="label"
                          sx={{ ...uploadTileSx, width: 200, height: 120 }}
                        >
                          <ImageOutlinedIcon
                            sx={{ fontSize: "24px", color: "#6C727F" }}
                          />
                          <Typography
                            component="span"
                            sx={{ fontSize: "12px", color: "#6C727F" }}
                          >
                            {t("manage_community_projects.modal.add_photo")}
                          </Typography>
                          <input
                            type="file"
                            hidden
                            accept={ALLOWED_IMAGE_TYPES.join(",")}
                            onChange={(e) => {
                              handleMainImagePick(e.target.files?.[0]);
                              e.target.value = "";
                            }}
                          />
                        </Button>
                      )}
                      {showError("mainImage") && (
                        <Typography
                          sx={{ fontSize: "13px", color: "#D8000C" }}
                        >
                          {errorText("mainImage")}
                        </Typography>
                      )}
                    </Box>

                    {/* Extra images */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <Typography sx={labelStyles}>
                        {t("manage_community_projects.modal.extra_images_label")}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        {(values.extraImages || []).map((img, idx) => (
                          <Box
                            key={`${img}-${idx}`}
                            sx={{
                              position: "relative",
                              width: 80,
                              height: 80,
                              borderRadius: "8px",
                              overflow: "hidden",
                              border: "1px solid #EAEAEA",
                            }}
                          >
                            <img
                              src={img}
                              alt={`extra-${idx}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <IconButton
                              onClick={() => handleRemoveExtraImage(idx)}
                              size="small"
                              sx={{
                                position: "absolute",
                                top: "2px",
                                insetInlineEnd: "2px",
                                backgroundColor: "rgba(0,0,0,0.4)",
                                color: "#fff",
                                padding: "2px",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.6)",
                                },
                              }}
                            >
                              <CloseIcon fontSize="inherit" />
                            </IconButton>
                          </Box>
                        ))}

                        {/* Add Photo tile */}
                        <Button
                          component="label"
                          sx={{ ...uploadTileSx, width: 80, height: 80 }}
                        >
                          <ImageOutlinedIcon
                            sx={{ fontSize: "20px", color: "#6C727F" }}
                          />
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "10px",
                              color: "#6C727F",
                              lineHeight: 1.1,
                              textAlign: "center",
                            }}
                          >
                            {t("manage_community_projects.modal.add_photo")}
                          </Typography>
                          <input
                            type="file"
                            hidden
                            accept={ALLOWED_IMAGE_TYPES.join(",")}
                            onChange={(e) => {
                              handleAddExtraImage(e.target.files?.[0]);
                              e.target.value = "";
                            }}
                          />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                  px: "24px",
                  py: "18px",
                  borderTop: "1px solid #EAEAEA",
                }}
              >
                <Button
                  onClick={onClose}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    fontSize: "15px",
                    fontWeight: 500,
                    borderRadius: "8px",
                    minWidth: "140px",
                    whiteSpace: "nowrap",
                    backgroundColor: "rgba(150, 153, 164, 1)",
                    color: "#fff",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "rgba(150, 153, 164, 0.9)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {t("manage_community_projects.modal.back_without_saving")}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{
                    textTransform: "none",
                    fontSize: "15px",
                    fontWeight: 500,
                    borderRadius: "8px",
                    minWidth: "140px",
                    whiteSpace: "nowrap",
                    backgroundColor: "rgba(37, 124, 109, 1)",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "rgba(37, 124, 109, 0.9)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : isEdit ? (
                    t("manage_community_projects.modal.save")
                  ) : (
                    t("manage_community_projects.modal.create")
                  )}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default ProjectModal;
